import { useCallback, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import screens from '../..';
import useFeatureFlags from '../../../hooks/useFeatureFlags';
import { setTutorialStep } from '../../../state/slices/tutorial/tutorial';
import QueryParamProxy from '../../../utils/queryParamProxy';
import { MissionTutorialStepsKeys, shouldChangeTutorialStep } from '../../../utils/tutorial';
import { BATTLES_FEATURE_STATUS } from '../../Battles/constants/dictionary';
import { sendMissionsEvent } from '../analytics/missionsLogger';
import { MISSIONS_EVENT_TYPES, MISSIONS_GAMEPLAY_STATUS, MISSIONS_STATES, MISSIONS_TYPES } from '../constants';
import { makeMissionsDataSelector, makeTutorialDataSelector } from '../selectors';
import { hasUnderMaintenanceState } from '../utils/missionsPerformance';
import useMissionsIconTooltip from './useMissionsIconTooltip';
import { checkAvailableGames, isBattleStreakAlreadyClaimed, isCurrentGamePlayable } from '../../Battles/utils/helpers';
export default function useMissionsIcon(_ref) {
  var navigation = _ref.navigation;
  var dispatch = useDispatch();
  var _useFeatureFlags = useFeatureFlags(),
    hasFeature = _useFeatureFlags.hasFeature;
  var selectMissionsData = useMemo(function () {
    return makeMissionsDataSelector(hasFeature);
  }, [hasFeature]);
  var _useSelector = useSelector(makeTutorialDataSelector, shallowEqual),
    activeTutorial = _useSelector.activeTutorial,
    tutorialStep = _useSelector.tutorialStep,
    currentMission = _useSelector.currentMission;
  var _useSelector2 = useSelector(selectMissionsData, shallowEqual),
    showMissionsButton = _useSelector2.showMissionsButton,
    shouldRunMissionsAnimation = _useSelector2.shouldRunMissionsAnimation,
    missionsIcon = _useSelector2.missionsIcon,
    missionsAssets = _useSelector2.missionsAssets,
    missionsState = _useSelector2.missionsState;
  var _useMissionsIconToolt = useMissionsIconTooltip({
      missionsState: missionsState,
      navigation: navigation
    }),
    triggerTooltip = _useMissionsIconToolt.triggerTooltip;
  var hasSentEvent = useRef(false);
  useEffect(function () {
    if (!hasSentEvent.current && showMissionsButton) {
      sendMissionsEvent(MISSIONS_EVENT_TYPES.DISPLAY_MISSIONS_ICON);
      hasSentEvent.current = true;
    }
  }, [showMissionsButton]);
  var targetScreen = useMemo(function () {
    var _missionsState$data;
    if (activeTutorial) return screens.Missions;
    if ((currentMission === null || currentMission === void 0 ? void 0 : currentMission.missionStatus) === MISSIONS_STATES.RUNNING) return currentMission.missionType === MISSIONS_TYPES.EXPLORER ? screens.Missions : screens.Battles;
    var _ref2 = (_missionsState$data = missionsState === null || missionsState === void 0 ? void 0 : missionsState.data) !== null && _missionsState$data !== void 0 ? _missionsState$data : {},
      battlesSetup = _ref2.battlesSetup,
      missionsFeature = _ref2.missionsFeature,
      currentCycle = _ref2.currentCycle,
      _ref2$currentStreak = _ref2.currentStreak,
      currentStreak = _ref2$currentStreak === void 0 ? null : _ref2$currentStreak,
      _ref2$currentGame = _ref2.currentGame,
      currentGame = _ref2$currentGame === void 0 ? null : _ref2$currentGame;
    if (!isCurrentGamePlayable(currentGame) && checkAvailableGames(currentCycle) && !isBattleStreakAlreadyClaimed(currentStreak)) return screens.Missions;
    if ((battlesSetup === null || battlesSetup === void 0 ? void 0 : battlesSetup.runningStatus) === BATTLES_FEATURE_STATUS.RUNNING) return screens.Battles;
    if ((missionsFeature === null || missionsFeature === void 0 ? void 0 : missionsFeature.missionsGameplayStatus) === MISSIONS_GAMEPLAY_STATUS.RUNNING || (missionsFeature === null || missionsFeature === void 0 ? void 0 : missionsFeature.missionsGameplayStatus) === MISSIONS_GAMEPLAY_STATUS.TUTORIAL) return screens.Missions;
    return screens.Battles;
  }, [activeTutorial, currentMission, missionsState]);
  var handleMissionButton = useCallback(function () {
    if (hasUnderMaintenanceState(missionsState)) triggerTooltip();else {
      if (activeTutorial && targetScreen === screens.Missions) {
        sendMissionsEvent(MISSIONS_EVENT_TYPES.MISSIONS_TUTORIAL_NEXT_STEP, {
          step: tutorialStep
        });
        shouldChangeTutorialStep(tutorialStep) && dispatch(setTutorialStep(MissionTutorialStepsKeys.CHECK_GOAL));
      }
      QueryParamProxy.navigate(navigation, targetScreen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionsState, activeTutorial, targetScreen, tutorialStep]);
  return useMemo(function () {
    return {
      showMissionsButton: showMissionsButton,
      shouldRunMissionsAnimation: shouldRunMissionsAnimation,
      missionsIcon: missionsIcon,
      missionsAssets: missionsAssets,
      missionsState: missionsState,
      handleMissionButton: handleMissionButton
    };
  }, [showMissionsButton, shouldRunMissionsAnimation, missionsIcon, missionsAssets, missionsState, handleMissionButton]);
}